import { useEffect, useRef, useState } from "react";
import { useApiClient } from "../../../api";
import { cleanDocument, safeAnswerDocument } from "../utils/answerDocument";

const content = (text, document) => ({
  text: text ?? "",
  document: cleanDocument(safeAnswerDocument(document, text ?? "")),
});
const snapshot = (value) => JSON.stringify(value);

export function useAnswerDraft({
  assignmentQuestion,
  student,
  tokenUser,
  token,
  readOnly = false,
}) {
  const api = useApiClient();
  const apiRef = useRef(api);
  apiRef.current = api;
  const sessionRef = useRef(null);
  const [view, setView] = useState(null);
  const key = JSON.stringify([assignmentQuestion, student, tokenUser, token]);
  const accessRef = useRef(readOnly);
  accessRef.current = readOnly;

  const publish = (session) => {
    if (session.active && sessionRef.current === session)
      setView({ ...session });
  };
  const dirty = (session) => snapshot(session.content) !== session.saved;

  const load = async (session) => {
    session.loading = true;
    session.error = "";
    publish(session);
    try {
      const draft = await apiRef.current.assignment.getAnswerDraft(
        session.target,
      );
      if (!session.active) return;
      if (!draft) throw new Error("Could not load your draft.");
      session.content =
        draft.answer !== null && draft.answer !== undefined
          ? content(draft.answer, draft.answerDocument)
          : content(draft.submittedAnswer, draft.submittedDocument);
      session.saved = snapshot(session.content);
      session.revision = draft.revision;
      session.hasDraft = draft.answer !== null && draft.answer !== undefined;
      session.loaded = true;
      session.conflict = false;
    } catch {
      session.loaded = false;
      session.error = "Could not load your draft. Retry before editing.";
    } finally {
      session.loading = false;
      publish(session);
    }
  };

  const save = async (session, manual = false) => {
    if (session.pending) await session.pending;
    if (
      !session.active ||
      !session.loaded ||
      session.loading ||
      session.conflict ||
      accessRef.current
    )
      return false;
    if (!manual && session.error) return false;
    if (!dirty(session)) return true;
    const savedContent = session.content;
    session.saving = true;
    session.error = "";
    publish(session);
    session.pending = (async () => {
      try {
        const result = await apiRef.current.assignment.saveAnswerDraft({
          ...session.target,
          answer: savedContent.text,
          answerDocument: savedContent.document,
          expectedRevision: session.revision,
        });
        if (!session.active) return false;
        if (!result.success) {
          session.conflict = Boolean(result.conflict);
          throw new Error(
            result.message || "Could not save your draft. Please retry.",
          );
        }
        session.revision = result.draft.revision;
        session.saved = snapshot(savedContent);
        session.hasDraft = true;
        return true;
      } catch (error) {
        session.error =
          error.message || "Could not save your draft. Please retry.";
        return false;
      } finally {
        session.saving = false;
        session.pending = null;
        publish(session);
      }
    })();
    return session.pending;
  };

  useEffect(() => {
    const session = {
      key,
      target: { assignmentQuestion, student, tokenUser, token },
      active: true,
      loading: true,
      loaded: false,
      content: content("", null),
      saved: snapshot(content("", null)),
      revision: 0,
      error: "",
      conflict: false,
      saving: false,
      submitting: false,
      pending: null,
      hasDraft: false,
    };
    sessionRef.current = session;
    publish(session);
    if (assignmentQuestion && student) load(session);
    const timer = setInterval(() => {
      if (!session.submitting && !session.pending) save(session);
    }, 10000);
    return () => {
      session.active = false;
      clearInterval(timer);
    };
    // Identity changes replace the session; callbacks use current API/settings refs.
  }, [key]);

  const current = view?.key === key ? view : null;
  return {
    text: current?.content.text ?? "",
    document: current?.content.document ?? null,
    dirty: current ? dirty(current) : false,
    loading: !current?.loaded || Boolean(current?.loading),
    saving: Boolean(current?.saving),
    submitting: Boolean(current?.submitting),
    error: current?.error || "",
    conflict: Boolean(current?.conflict),
    status:
      current?.error ||
      (!current?.loaded || current.loading
        ? "Loading draft…"
        : current.saving
          ? "Saving…"
          : dirty(current)
            ? "Unsaved changes"
            : current.hasDraft
              ? "Draft saved"
              : "No unsaved changes"),
    change: (text, document) => {
      const session = sessionRef.current;
      if (
        !session?.loaded ||
        session.loading ||
        session.submitting ||
        accessRef.current
      )
        return;
      session.content = content(text, document);
      publish(session);
    },
    save: () => save(sessionRef.current, true),
    reload: () => load(sessionRef.current),
    submit: async () => {
      const session = sessionRef.current;
      if (
        !session?.loaded ||
        session.loading ||
        session.submitting ||
        session.conflict ||
        accessRef.current
      )
        return null;
      session.submitting = true;
      publish(session);
      try {
        if (!(await save(session, true)) || !session.active) return null;
        const result = await apiRef.current.assignment.submitAnswer({
          ...session.target,
          answer: session.content.text,
          answerDocument: session.content.document,
          draftRevision: session.revision,
        });
        if (!session.active) return null;
        if (result.success) {
          session.revision = result.draftRevision;
          session.saved = snapshot(session.content);
          session.hasDraft = false;
          session.error = "";
        } else {
          session.conflict = Boolean(result.conflict);
          session.error = result.message || "Could not submit your answer.";
        }
        return result;
      } catch {
        session.error =
          "Could not confirm submission. Reload to check the saved answer before trying again.";
        session.conflict = true;
        return null;
      } finally {
        session.submitting = false;
        publish(session);
      }
    },
  };
}
